import React from 'react';
import { Link } from 'react-router-dom';
import { homePage, siteConfig } from '../data/mockData';

export const HomePage: React.FC = () => {
  return (
    <div className="bg-background-light">
      {/* Hero Section */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wide">
                <span className="material-symbols-outlined text-sm">verified_user</span>
                {homePage.hero.badge}
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.15] tracking-tight">
                Welcome To <br /><span className="text-primary">Spedcare Services</span>
              </h1>
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
                {homePage.hero.subheadline}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link
                  to="/services"
                  className="bg-primary hover:bg-primary-dark text-white px-7 py-3.5 rounded-md text-base font-medium transition-colors text-center"
                >
                  {homePage.hero.ctaPrimary}
                </Link>
                <Link
                  to="/about"
                  className="border border-slate-300 text-slate-700 hover:border-primary hover:text-primary px-7 py-3.5 rounded-md text-base font-medium transition-colors text-center"
                >
                  {homePage.hero.ctaSecondary}
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src={homePage.hero.image.src}
                  alt={homePage.hero.image.alt}
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPEED Values Section */}
      <section className="py-20 bg-primary-deep">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-accent-gold uppercase tracking-wide mb-3">
              {homePage.values.subtitle}
            </p>
            <h3 className="text-3xl lg:text-4xl font-bold text-white tracking-tight">
              {homePage.values.title}
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {homePage.values.items.map((value) => (
              <div
                key={value.letter}
                className="bg-white/5 border border-white/10 p-6 rounded-xl hover:bg-white/10 transition-colors"
              >
                <div className="w-10 h-10 bg-accent-gold/15 rounded-lg flex items-center justify-center text-accent-gold mb-5">
                  <span className="material-symbols-outlined text-xl">{value.icon}</span>
                </div>
                <h4 className="text-base font-semibold mb-2 text-white">{value.title}</h4>
                <p className="text-sm text-white/70 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NDIS Services Grid */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold text-primary uppercase tracking-wide mb-3">
                {homePage.services.subtitle}
              </p>
              <h3 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
                {homePage.services.title}
              </h3>
            </div>
            <Link
              to="/services"
              className="text-primary font-semibold inline-flex items-center gap-1.5 group"
            >
              {homePage.services.cta}
              <span className="material-symbols-outlined text-xl group-hover:translate-x-0.5 transition-transform">
                arrow_forward
              </span>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {homePage.services.items.map((service) => (
              <div
                key={service.title}
                className="bg-white p-7 rounded-xl border border-slate-200 hover:border-primary/40 hover:shadow-md transition-all"
              >
                <div className="w-11 h-11 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-5">
                  <span className="material-symbols-outlined text-2xl">{service.icon}</span>
                </div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3">{service.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 lg:py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            <div className="space-y-5">
              <div className="flex items-center gap-3 text-primary">
                <span className="material-symbols-outlined text-2xl">flag</span>
                <h3 className="text-2xl font-bold tracking-tight text-slate-900">
                  {homePage.mission.title}
                </h3>
              </div>
              <p className="text-base lg:text-lg text-slate-600 leading-relaxed">{homePage.mission.content}</p>
              <ul className="space-y-3 pt-1">
                {homePage.mission.points.map((point, index) => (
                  <li key={index} className="flex gap-3 text-slate-700">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary flex-shrink-0 mt-0.5">
                      <span className="material-symbols-outlined text-sm">check</span>
                    </span>
                    <span className="leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-5">
              <div className="flex items-center gap-3 text-primary">
                <span className="material-symbols-outlined text-2xl">visibility</span>
                <h3 className="text-2xl font-bold tracking-tight text-slate-900">
                  {homePage.vision.title}
                </h3>
              </div>
              <p className="text-base lg:text-lg text-slate-600 leading-relaxed">{homePage.vision.content}</p>
              <div className="p-6 bg-white rounded-xl border border-slate-200">
                <div className="flex gap-3">
                  <span className="material-symbols-outlined text-primary text-2xl">format_quote</span>
                  <p className="italic text-slate-600 leading-relaxed">{homePage.vision.quote}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-5">
            {homePage.cta.headline}
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-9">
            {homePage.cta.subheadline}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/contact"
              className="bg-white text-primary hover:bg-slate-100 px-8 py-3.5 rounded-md font-semibold text-base transition-colors text-center"
            >
              {homePage.cta.primary}
            </Link>
            <a
              href={`tel:${siteConfig.contact.phone.replace(/\D/g, '')}`}
              className="border border-white/40 text-white hover:bg-white/10 px-8 py-3.5 rounded-md font-semibold text-base transition-colors text-center"
            >
              {homePage.cta.secondary}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
